import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IconEditConnector } from "@multiweb/reactConnectors/IconEditConnector";
import { LinkButtonConnector } from "@multiweb/reactConnectors/LinkButtonConnector";
import { canCallOnlyStaticMethods } from "@multiweb/reactConnectors/LinkButtonConnector/utils";
import { UrlUtils } from "Global/Base/Utils/UrlUtils";
import { LogicalOperator } from "../../../Global/Base/LogicalOperations";
import { RightType } from "../../../Global/Base/Rights/RightTypes";
import { IInteractive } from "../../CommonUIInterfaces";
import { IEnabledVisualControl } from "../../UI.BaseControls/ControlInterfaces";
import { ActionType, IMultiObjectAction, SelectionType } from "../../UI.Controls/Actions/IAction";
import { ILogicalComponent } from "../LogicalTree/ILogicalComponent";
import { Control } from "../UI/Controls/Control";
import { IControl, IEditableControl, IMultiEdit, IRunWithoutEdit, IStaticControl, IVersioned } from "../UI/Controls/ControlInterfaces";
import { EditableControl } from "../UI/Controls/EditableControl";
import { LOVEdit } from "../UI/Controls/LOVEdit";
import { BatchDownloadAction } from "../UI/XGControls/Actions/BatchDownloadAction";
import { CryptoAction } from "../UI/XGControls/Actions/CryptoAction";
import { CryptoButton } from "../UI/XGControls/Actions/CryptoButton";
import { CryptoLabel } from "../UI/XGControls/Actions/CryptoLabel";
import { CryptoLabelAction } from "../UI/XGControls/Actions/CryptoLabelAction";
import { DetailFormAction } from "../UI/XGControls/Actions/DetailFormAction";
import { FormButton } from "../UI/XGControls/Actions/FormButton";
import { IMultiActionToolbarButton } from "../UI/XGControls/Actions/IToolbarButton";
import { MethodAction } from "../UI/XGControls/Actions/MethodAction";
import { MethodButton } from "../UI/XGControls/Actions/MethodButton";
import { MultiActionToolbarButton } from "../UI/XGControls/Actions/MultiActionToolbarButton";
import { MultiObjectAction } from "../UI/XGControls/Actions/MultiObjectAction";
import { NodeAction } from "../UI/XGControls/Actions/NodeAction";
import { ParametricFormAction } from "../UI/XGControls/Actions/ParametricFormAction";
import { ParametricFormButton } from "../UI/XGControls/Actions/ParametricFormButton";
import { ReportAction } from "../UI/XGControls/Actions/ReportAction";
import { ShowInsertAction } from "../UI/XGControls/Actions/ShowInsertAction";
import { StandardAction } from "../UI/XGControls/Actions/StandardAction";
import { StandardRecordAction } from "../UI/XGControls/Actions/StandardRecordAction";
import { XGAction } from "../UI/XGControls/Actions/XGAction";
import { XGButton } from "../UI/XGControls/Actions/XGButton";
import { IMultiRecordsComponent } from "../UI/XGControls/Frames/FrameInterfaces";
import { IEditableFrame } from "../UI/XGControls/Frames/IEditableFrame";
import { IFrame } from "../UI/XGControls/IFrame";
import { IBasicRightResults } from "./BasicRightsResults";
import { RightsEvaluationOverridesBase } from "./RightsEvaluationOverridesBase";

const logger = LazyLoadingSectionLogger.create(LoggingSection.rights);

/**
 * Registrované override metody by neměly mít vedlejší efekt.
 * Třídy dědící z těchto overrides, přepisující jejich metody a provolávající metody z rodiče musí zajistit, že se budou provolávat pouze se správným typem komponenty v parametru.
 */

export class RightsEvaluationOverrides extends RightsEvaluationOverridesBase {
    private _PRINT_CONSOLE_DEBUG_LEVEL: number = 5; // TODO-rites: odstranit nebo aspoň vypnout

    protected override _initializeOverrides(): void {
        super._initializeOverrides();

        //#region RunWithoutEdit
        this._registerOverrideFunctionForRightType(MethodAction, RightType.enabled, this._runWithoutEditActionsEnabled, true);
        this._registerOverrideFunctionForRightType(ParametricFormAction, RightType.enabled, this._runWithoutEditActionsEnabled);
        //this._registerOverrideFunctionForRightType(ParametricFormButton, RightType.enabled, this._runWithoutEditActionsEnabled);
        this._registerOverrideFunctionForRightType(XGButton, RightType.enabled, this._runWithoutEditActionsEnabled, true);
        //#endregion RunWithoutEdit

        this._registerOverrideFunctionForRightType(DetailFormAction, RightType.enabled, this._detailFormActionEnabled);

        this._registerOverrideFunctionForRightType(FormButton, RightType.enabled, this._formButtonEnabled);

        this._registerOverrideFunctionForRightType(IconEditConnector, RightType.enabled, this._iconEditConnectorEnabled);

        this._registerOverrideFunctionForRightType(ParametricFormAction, RightType.enabled, this._parametricFormActionEnabled);
        this._registerOverrideFunctionForRightType(ParametricFormAction, RightType.visible, this._parametricFormActionVisible);

        this._registerOverrideFunctionForRightType(ParametricFormButton, RightType.enabled, this._parametricFormButtonEnabled);

        this._registerOverrideFunctionForRightType(MethodAction, RightType.visible, this._methodActionVisible);
        this._registerOverrideFunctionForRightType(MethodAction, RightType.enabled, this._methodActionEnabled);

        this._registerOverrideFunctionForRightType(MethodButton, RightType.visible, this._methodButtonVisible);
        this._registerOverrideFunctionForRightType(MethodButton, RightType.enabled, this._methodButtonEnabled);

        this._registerOverrideFunctionForRightType(ShowInsertAction, RightType.enabled, this._showInsertActionEnabled);

        this._registerOverrideFunctionForRightType(ReportAction, RightType.enabled, this._reportActionEnabled);

        this._registerOverrideFunctionForRightType(CryptoLabelAction, RightType.visible, this._cryptoLabelActionVisible);
        this._registerOverrideFunctionForRightType(CryptoLabelAction, RightType.enabled, this._cryptoLabelActionEnabled);

        this._registerOverrideFunctionForRightType(CryptoLabel, RightType.visible, this._cryptoLabelVisible);
        this._registerOverrideFunctionForRightType(CryptoLabel, RightType.enabled, this._cryptoLabelEnabled);

        this._registerOverrideFunctionForRightType(CryptoAction, RightType.visible, this._cryptoActionVisible);
        this._registerOverrideFunctionForRightType(CryptoAction, RightType.enabled, this._cryptoActionEnabled);

        this._registerOverrideFunctionForRightType(CryptoButton, RightType.visible, this._cryptoButtonVisible);
        this._registerOverrideFunctionForRightType(CryptoButton, RightType.enabled, this._cryptoButtonEnabled);

        this._registerIgnoredType(BatchDownloadAction);

        this._registerOverrideFunctionForRightType(StandardAction, RightType.insert, this._standardActionsInsert);
        this._registerOverrideFunctionForRightType(StandardAction, RightType.edit, this._standardActionsEdit, true);
        this._registerOverrideFunctionForRightType(StandardAction, RightType.delete, this._standardActionsDelete, true);
        this._registerOverrideFunctionForRightType(StandardAction, RightType.enabled, this._standardActionsEnabled);
        this._registerOverrideFunctionForRightType(StandardAction, RightType.visible, this._standardActionsVisible);

        this._registerOverrideFunctionForRightType(StandardRecordAction, RightType.enabled, this._standardActionsEnabled);
        this._registerOverrideFunctionForRightType(StandardRecordAction, RightType.visible, this._standardActionsVisible);

        this._registerOverrideFunctionForRightType(MultiActionToolbarButton, RightType.visible, this._multiActionToolbarButtonVisible);
        this._registerOverrideFunctionForRightType(MultiActionToolbarButton, RightType.enabled, this._multiActionToolbarButtonVisible);

        this._registerOverrideFunctionForRightType(NodeAction, RightType.visible, this._nodeActionVisible);

        this._registerOverrideFunctionForRightType(EditableControl, RightType.editing, this._editableControlsEditing, true);
        this._registerOverrideFunctionForRightType(Control, RightType.multiEditing, this._controlsMultiEditing, true);

        this._registerOverrideFunctionForRightType(LOVEdit, RightType.enabled, this._lovEditEnabled);

        //this._registerOverrideFunctionForRightType(EditableControl, RightType.enabledVisual, this._enabledVisualControlsEnabledVisual, true);
        this._registerInterfaceType(IEnabledVisualControl);
        this._registerOverrideFunctionForRightType(IEnabledVisualControl, RightType.enabledVisual, this._enabledVisualControlsEnabledVisual, true);
        this._registerOverrideFunctionForRightType(LinkButtonConnector, RightType.enabled, this._linkButtonEnabled);

        // TODO Prava pro EditableControlConnector - v budoucnu by se mel sloucit s EditableControl, aby se nehandloval zvlast Control/EditableControl a ControlConnector/EditableControlConnector.
        this._registerInterfaceType(IEditableControl);
        this._registerOverrideFunctionForRightType(IEditableControl, RightType.editing, this._editableControlsEditing, true);
        this._registerInterfaceType(IControl);
        this._registerOverrideFunctionForRightType(IControl, RightType.multiEditing, this._controlsMultiEditing, true);

        //#region InterfaceOverrides
        // Registrace override metod k interfacům.
        // Před registrací override metody musíme zaregistrovat typ jako interfacový!
        //this._registerInterfaceType(IFrame);
        //this._registerOverrideFunctionForRightType(IFrame, RightType.interactive, this._iFrameInteractive, true);

        this._registerInterfaceType(IInteractive);
        this._registerOverrideFunctionForRightType(IInteractive, RightType.interactive, this._iInteractiveInteractive, true); //TODO-rites: !! Wat ? Overrides asi nežerou interfacy - mrknout na overridesBase.getOverridesForComponent
        //#endregion InterfaceOverrides
    }

    protected override _initializeOperators(): void {
        super._initializeOperators();

        this._operatorsForRightTypes.set(RightType.enabled, LogicalOperator.or); // TODO-rites: udělat metodu místo setu?; c2: tak jakej?..
    }

    protected _runWithoutEditActionsEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null, _allBasicRightsResult?: IBasicRightResults): boolean | null {
        this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for runWithoutEdit right for ${controlWrapper.control.constructor.name}. OwnerFrame enabled is "${this._frame.enabled}".`, 10); // ` TODO-rites: remove !!
        const control = controlWrapper.control as Control;
        let xgAction: XGAction | null;
        let action: IRunWithoutEdit | null;

        if (control instanceof XGButton) {
            xgAction = control.xgAction;

            if (xgAction instanceof IRunWithoutEdit) {
                action = xgAction;
            }
        }

        if (!action && control instanceof IRunWithoutEdit) {
            action = control;
        }

        if (action?.runWithoutEdit && basicRightResult && !this._isFrameVersioned(xgAction?.executionContext.ownerFrame)) {
            return true;
        }

        // // Method a Parametric akce muzou byt aktivni i kdyz nemam pravo editovat (musí ale být povolena právy ze serveru, jinak se nemá runWithoutEdit aplikovat)
        // if (!action.runWithoutEdit // Zajímá nás, jen pokud je RunWithoutEdit == true; Hodnota false nemá mít žádný efekt
        //     || basicRightResult) { // Pokud je akce povolena, tak nemá smysl nic dalšího dělat)
        //     return null;
        // }

        // let disabledByItemRights: boolean | null;

        // if (allBasicRightsResult) {
        //     disabledByItemRights = allBasicRightsResult.getAdditionalResultInfo(AdditionalRightResultInfoType.disabledByItemRights);
        // }

        // if ((controlWrapper.classItemRights?.Enabled ?? true) // Pokud je akce zakázána serverem, nejde povolit ani přes runWithoutEdit
        //         && !disabledByItemRights // Zohledňovat RWE má smysl jen pokud není akce zakázána právy na prvek (na úrovni formuláře nebo např. ReadOnlyIf)
        //         && !this._frame.actualObjectStandardRights.canEdit
        //         && action
        //         && !action.isStatic) {
        //     return this._frame.interactive; // Dříve tu bylo "this._frame.isActive", ale to reflektuje "this._frame.enabled", který je při zakázané editaci == false
        // }

        return null;
    }

    //#region DetailFormAction
    private _detailFormActionEnabledInternal(detailFormAction: DetailFormAction, basicRightResult?: boolean | null): boolean | null {
        if (!detailFormAction || basicRightResult === false) // Pokud neexistuje nebo pokud byla akce vyhodnocena jako neaktivní už v základním vyhodnocení práv, nebude se dál pokračovat
            return null;

        if (detailFormAction.detailAssocName || detailFormAction.conditionParams) {
            const { ownerFrame, actualObject } = detailFormAction.executionContext;
            let rightResult = actualObject != null && this._frame.interactive;
            if (ownerFrame instanceof IEditableFrame) {
                rightResult = rightResult && (!detailFormAction.enableInEdit || (!ownerFrame || ownerFrame.isEditing)); // Pokud jde o editovatelný seznam a akce má EnableInEdit == True, potom bude akce mimo editaci neaktivní
            }

            return rightResult;
        }

        return null;
    }

    protected _detailFormActionEnabled(controlWrapper: ILogicalComponent, basicRightResult: boolean | null): boolean | null {
        const detailFormAction = controlWrapper.control as DetailFormAction;

        return this._detailFormActionEnabledInternal(detailFormAction, basicRightResult);
    }
    //#endregion DetailFormAction

    //#region FormButton
    protected _formButtonEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const formButton = controlWrapper.control as FormButton;
        const formAction = formButton.xgAction as DetailFormAction;

        return this._detailFormActionEnabledInternal(formAction, basicRightResult);
    }
    //#endregion FormButton

    //#endregion ParametricFormAction
    private _parametricFormActionEnabledInternal(parametricFormAction: ParametricFormAction, basicRightResult?: boolean | null): boolean | null {
        if (!parametricFormAction || basicRightResult === false) // Pokud neexistuje nebo pokud byla akce vyhodnocena jako neaktivní už v základním vyhodnocení práv, nebude se dál pokračovat
            return null;

        if (parametricFormAction.linkAttrForSelObj) {
            const { ownerFrame, actualObject } = parametricFormAction.executionContext;
            let rightResult = actualObject != null && (!ownerFrame || ownerFrame.enabled) && this._frame.interactive && !this._isFrameVersioned(ownerFrame);
            if (ownerFrame instanceof IEditableFrame) {
                rightResult = rightResult && (!parametricFormAction.enableInEdit || (!ownerFrame || ownerFrame.isEditing)); // Pokud jde o editovatelný seznam a akce má EnableInEdit == True, potom bude akce mimo editaci neaktivní
            }

            return rightResult;
        } else {
            return true;
        }
    }

    protected _parametricFormActionEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const parametricFormAction = controlWrapper.control as ParametricFormAction;

        return this._parametricFormActionEnabledInternal(parametricFormAction, basicRightResult);
    }

    protected _parametricFormActionVisible(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const parametricFormAction = controlWrapper.control as ParametricFormAction;

        if (parametricFormAction && basicRightResult)
            return !this._isFrameVersioned(parametricFormAction.executionContext.ownerFrame);

        return null;
    }
    //#endregion ParametricFormAction

    // #region ParametricFormButton
    protected _parametricFormButtonEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const parametricFormButton = controlWrapper.control as ParametricFormButton;
        const parametricFormAction = parametricFormButton.xgAction as ParametricFormAction;

        return this._parametricFormActionEnabledInternal(parametricFormAction, basicRightResult);
    }
    // #endregion ParametricFormButton

    //#region MethodAction
    private static _methodActionVisibleInternal(methodAction: MethodAction, basicRightResult?: boolean | null): boolean | null {
        if (basicRightResult === false)
            return null;

        if (methodAction && !methodAction.isStatic)
            return basicRightResult; //O viditelnost se postará showIfDefault, který se nastavuje v RightsManager._frameRightsEvaluator()

        return null;
    }

    private _methodActionEnabledInternal(methodAction: MethodAction, basicRightResult?: boolean | null): boolean | null {
        if (basicRightResult === false)
            return null;

        if (methodAction && !methodAction.isStatic) {
            const { ownerFrame, actualObject } = methodAction.executionContext;
            let rightResult = actualObject != null && (!ownerFrame || ownerFrame.enabled) && this._frame.interactive && !this._isFrameVersioned(ownerFrame);
            if (ownerFrame instanceof IEditableFrame) {
                rightResult = rightResult && (!methodAction.enableInEdit || (!ownerFrame || ownerFrame.isEditing)); // Pokud jde o editovatelný seznam a akce má EnableInEdit == True, potom bude akce mimo editaci neaktivní
            }

            return rightResult;
        }

        return null;
    }

    protected _methodActionEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const methodAction = controlWrapper.control as MethodAction;

        return this._methodActionEnabledInternal(methodAction, basicRightResult);
    }

    protected _methodActionVisible(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const methodAction = controlWrapper.control as MethodAction;

        return !this._isFrameVersioned(methodAction.executionContext.ownerFrame) && RightsEvaluationOverrides._methodActionVisibleInternal(methodAction, basicRightResult);
    }
    //#endregion MethodAction

    // #region MethodButton
    protected _methodButtonVisible(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const methodButton = controlWrapper.control as MethodButton;
        const methodAction = methodButton.xgAction as MethodAction;

        return RightsEvaluationOverrides._methodActionVisibleInternal(methodAction, basicRightResult);
    }

    protected _methodButtonEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const methodButton = controlWrapper.control as MethodButton;
        const methodAction = methodButton.xgAction as MethodAction;

        return this._methodActionEnabledInternal(methodAction, basicRightResult);
    }
    // #endregion MethodButton

    // #region ShowInsertAction
    protected async _showInsertActionEnabled(controlWrapper: ILogicalComponent): Promise<boolean | null> {
        const showInsertAction = controlWrapper.control as ShowInsertAction;

        return await showInsertAction.evaluateInsertRight();
    }
    // #endregion ShowInsertAction

    // #region ReportAction
    protected _reportActionEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const reportAction = controlWrapper.control as ReportAction;

        if (reportAction) {
            const result = !!this._frame.actualObject && this._frame.interactive;
            if (basicRightResult != null) return result && basicRightResult;

            return result;
        }

        return null;
    }
    // #endregion ReportAction

    // #region IconEditConnector
    protected _iconEditConnectorEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null): boolean | null {
        const iconEditConnector = controlWrapper.control as IconEditConnector;

        if (basicRightResult === false)
            return null;

        if (iconEditConnector)
            return !!iconEditConnector.editWindow && !!iconEditConnector.assocName;

        return null;
    }
    // #endregion IconEditConnector

    // #region CryptoLabelAction
    private _cryptoLabelActionVisibleInternal(cryptoLabelAction: CryptoLabelAction): boolean | null {
        if (cryptoLabelAction)
            return !!this._frame.actualObject;

        return null;
    }

    private _cryptoLabelActionEnabledInternal(cryptoLabelAction: CryptoLabelAction): boolean | null {
        if (cryptoLabelAction)
            return !!this._frame.actualObject && this._frame.isActive/* && this._frame.interactive TODO-rites: X odstranit/nahradit "isActive"*/;

        return null;
    }

    protected _cryptoLabelActionVisible(controlWrapper: ILogicalComponent): boolean | null {
        const cryptoLabelAction = controlWrapper.control as CryptoLabelAction;

        return this._cryptoLabelActionVisibleInternal(cryptoLabelAction);
    }

    protected _cryptoLabelActionEnabled(controlWrapper: ILogicalComponent): boolean | null {
        const cryptoLabelAction = controlWrapper.control as CryptoLabelAction;

        return this._cryptoLabelActionEnabledInternal(cryptoLabelAction);
    }
    // #endregion CryptoLabelAction

    // #region CryptoLabel
    protected _cryptoLabelVisible(controlWrapper: ILogicalComponent): boolean | null {
        const cryptoLabel = controlWrapper.control as CryptoLabel;
        const cryptoLabelAction = cryptoLabel.xgAction as CryptoLabelAction;

        return this._cryptoLabelActionVisibleInternal(cryptoLabelAction);
    }

    protected _cryptoLabelEnabled(controlWrapper: ILogicalComponent): boolean | null {
        const cryptoLabel = controlWrapper.control as CryptoLabel;
        const cryptoLabelAction = cryptoLabel.xgAction as CryptoLabelAction;

        return this._cryptoLabelActionEnabledInternal(cryptoLabelAction);
    }
    // #endregion CryptoLabel

    // #region CryptoAction
    private async _cryptoActionVisibleInternal(cryptoAction: CryptoAction, basicRightResult: boolean): Promise<boolean | null> {
        if (cryptoAction) {
            const obj = cryptoAction.executionContext.actualObject;
            if (basicRightResult && !obj)
                return basicRightResult;
            else
                return await cryptoAction.evaluateVisibleRight(obj);
        }
        return null;
    }

    protected async _cryptoActionVisible(controlWrapper: ILogicalComponent, basicRightResult: boolean): Promise<boolean | null> {
        const cryptoAction = controlWrapper.control as CryptoAction;

        return this._cryptoActionVisibleInternal(cryptoAction, basicRightResult);
    }

    private async _cryptoActionEnabledInternal(cryptoAction: CryptoAction, basicRightResult: boolean): Promise<boolean | null> {
        if (cryptoAction) {
            const { ownerFrame, actualObject } = cryptoAction.executionContext;
            let rightResult = !basicRightResult
                ? basicRightResult
                : await cryptoAction.evaluateEnabledRight(actualObject) && (!ownerFrame || ownerFrame.enabled) && this._frame.interactive && !this._isFrameVersioned(ownerFrame);
            if (ownerFrame instanceof IEditableFrame) {
                rightResult = rightResult && (!cryptoAction.enableInEdit || ownerFrame.isEditing); // Pokud jde o editovatelný seznam a akce má EnableInEdit == True, potom bude akce mimo editaci neaktivní
            }
            return rightResult;
        }
        return null;
    }

    protected async _cryptoActionEnabled(controlWrapper: ILogicalComponent, basicRightResult: boolean): Promise<boolean | null> {
        const cryptoAction = controlWrapper.control as CryptoAction;

        return this._cryptoActionEnabledInternal(cryptoAction, basicRightResult);
    }
    // #endregion CryptoAction

    // #region CryptoButton
    protected _cryptoButtonVisible(controlWrapper: ILogicalComponent, basicRightResult: boolean): Promise<boolean | null> {
        const cryptoButton = controlWrapper.control as CryptoButton;
        const cryptoAction = cryptoButton.xgAction as CryptoAction;

        return this._cryptoActionVisibleInternal(cryptoAction, basicRightResult);
    }

    protected _cryptoButtonEnabled(controlWrapper: ILogicalComponent, basicRightResult: boolean): Promise<boolean | null> {
        const cryptoButton = controlWrapper.control as CryptoButton;
        const cryptoAction = cryptoButton.xgAction as CryptoAction;

        return this._cryptoActionEnabledInternal(cryptoAction, basicRightResult);
    }
    // #endregion CryptoButton

    //#region LOVEdit
    protected async _lovEditEnabled(controlWrapper: ILogicalComponent): Promise<boolean | null> {
        const lovEdit = controlWrapper.control as LOVEdit;
        if (lovEdit) {
            const className = await lovEdit.getClassName();
            if (className === "")
                return null;
            if ((lovEdit.editWindow != null && lovEdit.editWindow?.toUpperCase().startsWith("IMPLICIT")) || !className)
                return false;
        }
        return null;
    }
    //#endregion LOVEdit

    //#region StandardAction
    protected _standardActionsInsert(controlWrapper: ILogicalComponent, _basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): boolean | null {
        const standardAction = controlWrapper.control as StandardAction;
        //TODO-rites: reflektovat masterObject? viz MF._setRightsOnStandartActions; c2: nějak už je asi zohledněno v RightsManageru v getování IED rights
        if (standardAction && standardAction.actionType === ActionType.standardNewRecord) {
            if (!this._frame.masterFrame || this._frame.masterFrame.actualObject != null) { //pokud na mastru neni vybran zadny objekt, tak se na podrizenem seznamu nebude pokouset zaktivnit tlacitko
                return this._frame.actualObjectStandardRights.canNew && this._frame.interactive && !this._getDisabledFromParent(allBasicRightsResult);
            } else
                return false;
        } else if (standardAction && standardAction.actionType === ActionType.standardCopyRecord && this._frame instanceof IFrame) { // TODO-rites: tenhle blok tu možná nebude potřeba, protože v době kdy se vyhodnocují IED práva, tak akce kopírování asi ještě neexistují. Mohlo by stačit v _standardActionsEnabled
            return this._frame.hasSelectedRecord && this._frame.interactive && this._frame.actualObjectStandardRights.canNew && !this._frame.actualObject.isServerSideEncrypted && !this._getDisabledFromParent(allBasicRightsResult);
        }

        return null;
    }

    protected _standardActionsEdit(controlWrapper: ILogicalComponent, _basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): boolean | null {
        const standardAction = controlWrapper.control as StandardAction;
        if (standardAction?.actionType === ActionType.standardEditRecord && this._frame instanceof IFrame) {
            this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for edit right for StandardAction. Editable OwnerFrame canEdit is "${this._frame.actualObjectStandardRights.canEdit}".`, 10); // ` TODO-rites: remove !!
            return this._frame.hasSelectedRecord && this._frame.actualObjectStandardRights.canEdit && this._frame.interactive && !this._frame.actualObject.isClientSideEncrypted && !this._getDisabledFromParent(allBasicRightsResult);
        } else if (standardAction?.actionType === ActionType.standardEditRecords && this._frame instanceof IMultiRecordsComponent) {
            this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for edit (in multiedit) right for StandardAction. Editable OwnerFrame canEdit is "${this._frame.actualObjectStandardRights.canEdit}".`, 10); // ` TODO-rites: remove !!
            return this._frame.isMultiSelecting && this._frame.actualObjectStandardRights.canEdit && this._frame.interactive && !this._frame.actualObject.isClientSideEncrypted && !this._getDisabledFromParent(allBasicRightsResult);
        }
        return null;
    }

    protected _standardActionsDelete(controlWrapper: ILogicalComponent, _basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): boolean | null {
        const standardAction = controlWrapper.control as StandardAction;
        if (standardAction?.actionType === ActionType.standardDeleteRecord && this._frame instanceof IFrame) {
            return this._frame.hasSelectedRecord && this._frame.actualObjectStandardRights.canDelete && this._frame.interactive && !this._getDisabledFromParent(allBasicRightsResult);
        }
        if (standardAction?.actionType === ActionType.standardDeleteRecords && this._frame instanceof IMultiRecordsComponent) {
            return this._frame.isMultiSelecting && this._frame.actualObjectStandardRights.canDelete && this._frame.interactive && !this._getDisabledFromParent(allBasicRightsResult);
        }
        return null;
    }

    protected async _standardActionsEnabled(controlWrapper: ILogicalComponent, _basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): Promise<boolean | null> {
        this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for enabled right for StandardAction. OwnerFrame enabled is "${this._frame.enabled}".`, 10); // ` TODO-rites: remove !!
        const standardAction = controlWrapper.control as StandardAction;

        //if (standardAction.context === ActionContext.objectAction) //TODO-rites : pro které by tohle mělo platit?...a pokud, tak by toto mělo nýt buď níž nebo ještě opodmínkované
        //    return this._frame.actualObject != null;

        //if (standardAction.actionType === ActionType.standardOpenDetail)
        //    return null;

        if (this._frame instanceof IEditableFrame) {
            switch (standardAction.actionType) {
                case ActionType.standardSaveRecord:
                case ActionType.standardSaveAll:
                case ActionType.standardCancelChanges:
                case ActionType.standardCancelRecordChanges:
                    return (!!this._frame.isEditing || this._frame.isEditContextInitiator) && (!this._frame.hasHandedContext || !(await this._frame.supportsEditContext)); /*&& this._frame.isActive*/
                case ActionType.standardDeselectAll:
                case ActionType.standardSelectAll:
                    return this._frame instanceof IMultiRecordsComponent ? this._frame.isMultiSelectingMode /*&& this._frame.isActive*/ : false;
                //case ActionType.standardDeleteRecords:
                //    return this._frame instanceof IMultiRecordsComponent ? this._frame.isMultiSelectingMode /*&& this._frame.isActive*/ : false;
                case ActionType.standardRefreshList:
                case ActionType.standardRefreshRecord:
                    return !this._frame.isEditing;
                case ActionType.standardFilter:
                case ActionType.standardFilterList:
                    return true;
                case ActionType.standardOpenDetail:
                case ActionType.standardRowActions:
                    return !(this._frame instanceof IMultiRecordsComponent && this._frame.isMultiSelectingMode);
                    // return this._frame.masterFrame && !this._frame.masterObject ? false : !this._frame.isEditing /*&& this._frame.isActive*/; //TODO-rites c1: remove? při otevření detailu záznamu do modalu byla akce špatně vyhodnocena. c2: Který test to byl? děje se to pořád?
                    //return !this._frame.isEditing /*&& this._frame.isActive*/; //TODO-rites: v konfliktu s předchozím řádkem, na frm "dopravní prostředky" při nevybraném záznamu v hlavním seznamu a otevření záložky "provozní kniha" je akce refresh aktivní, ale neměla by být
                case ActionType.standardSaveRecordInEditContext:
                    return (await this._frame.supportsEditContext) && !!this._frame.isEditing && (this._frame.hasHandedContext || this._frame.useMasterContext);
            }
        }

        switch (standardAction.actionType) {
            case ActionType.standardInfoRecord:
            case ActionType.standardAuditInfoRecord:
            case ActionType.standardOpenDetailOnMainForm:
            case ActionType.standardShowWFHistory:
            case ActionType.standardShowWFParticipants:
            case ActionType.standardShowWFStatus:
                return this._frame.interactive;
            //case ActionType.standardRefreshList:
            //    return this._frame.isActive;
            case ActionType.standardCopyRecord: // TODO-rites: zkontrolovat, jestli není zbytečně v _standardActionsInsert
                return this._frame.hasSelectedRecord && this._frame.interactive && !this._getDisabledFromParent(allBasicRightsResult) && this._frame.actualObjectStandardRights.canNew && !this._frame.actualObject.isServerSideEncrypted;
            //case ActionType.standardSelectRecord:
            //    return this._frame.isActive; //TODO-rites: X nahradit propertou "isActive"
            //case ActionType.standardOpenDetail:
            //    return this._frame.isActive; //TODO-rites: X nahradit propertou "isActive"
        }

        return null;
    }

    protected async _standardActionsVisible(controlWrapper: ILogicalComponent): Promise<boolean | null> {
        this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for visible right for StandardAction. OwnerFrame enabled is "${this._frame.enabled}".`, 10); // ` TODO-rites: remove !!
        const standardAction = controlWrapper.control as StandardAction;

        if (this._frame instanceof IEditableFrame) { //TODO-rites: než takhle, radši udělat overrides každému příslušnému typu framu a natypovat this._frame?
            switch (standardAction.actionType) {
                case ActionType.standardSaveRecord:
                case ActionType.standardSaveAll:
                case ActionType.standardCancelChanges:
                case ActionType.standardCancelRecordChanges:
                    return (!!this._frame.isEditing || this._frame.isEditContextInitiator) && (!this._frame.hasHandedContext || !(await this._frame.supportsEditContext));
                case ActionType.standardDeleteRecord:
                case ActionType.standardEditRecord:
                case ActionType.standardNewRecord:
                case ActionType.standardCopyRecord:
                case ActionType.standardRefreshRecord:
                case ActionType.standardRefreshList:
                    if (this._frame instanceof IMultiRecordsComponent) {
                        return (!this._frame.isEditing && !this._frame.isMultiSelectingMode) || !this._frame.interactive;
                    } else {
                        return !this._frame.isEditing;
                    }
                case ActionType.standardDeleteRecords:
                case ActionType.standardEditRecords:
                    if (this._frame instanceof IMultiRecordsComponent) {
                        return (!this._frame.isEditing && this._frame.isMultiSelectingMode);
                    }
                    break;
                case ActionType.standardDeselectAll:
                case ActionType.standardSelectAll:
                    return this._frame instanceof IMultiRecordsComponent ? this._frame.isMultiSelectingMode : false;
                case ActionType.standardOpenDetail:
                case ActionType.standardRowActions:
                    return !(this._frame instanceof IMultiRecordsComponent && this._frame.isMultiSelectingMode);
                case ActionType.standardShowWFHistory:
                case ActionType.standardShowWFParticipants:
                case ActionType.standardShowWFStatus:
                    return this._frame.actualObjectHasWF;
                case ActionType.standardSaveRecordInEditContext:
                    return (await this._frame.supportsEditContext) && !!this._frame.isEditing && (this._frame.hasHandedContext || this._frame.useMasterContext);
            }
        }

        return null;
    }

    protected _nodeActionVisible(controlWrapper: ILogicalComponent): boolean | null {
        const nodeAction = controlWrapper.control as NodeAction;

        // NodeAction se skrývá pokud v sobě nemá viditelné akce, to ale u MultiObjectAction neplatí protože se její práva vyhodnocují různě na 2 místech
        // zvlášť pro akci samotnou a pak pro její MultiActionToolbarButton, proto zůstávala nodeAction stále viditelná
        // pokud NodeAction obsahuje alespoň jednu MultiObjectAction nebo action s 'single' nebo 'all' výběrem, tak ji zviditelníme
        let gotMultiObjectAction = false;
        for (const action of nodeAction.actions) {
            if ((action instanceof MultiObjectAction)) {
                gotMultiObjectAction = true;
            }

            if (action instanceof IStaticControl) {
                if (action.supportedSelectionModes === SelectionType.single || action.supportedSelectionModes === SelectionType.all) {
                    return true;
                }
            }
        }

        if (gotMultiObjectAction) {
            return nodeAction.owner instanceof IMultiRecordsComponent && nodeAction.owner.isMultiSelectingMode;
        }

        return null;
    }

    protected _multiActionToolbarButtonVisible(controlWrapper: ILogicalComponent, basicResult: boolean): boolean | null {
        const multiActionToolbarButton = controlWrapper.control as IMultiActionToolbarButton;
        const multiAction = multiActionToolbarButton.action;

        if (multiAction instanceof IMultiObjectAction) {
            // Je potreba nastavit i prava na multiakci, ktera ja pouzita v soucasnem nodeActionu, prava se puvodne
            // nastavuji jen na rodicovskou akci, dalo by se to pridat do stromu, ale prijde mi to zbytecne zesloziteni uz tak sloziteho stromu
            const resultValue = multiAction.owner instanceof IMultiRecordsComponent && multiAction.owner.isMultiSelectingMode && basicResult;
            multiAction.visible = multiAction.enabled = resultValue;
            return resultValue;
        }

        return null;
    }

    protected _editableControlsEditing(controlWrapper: ILogicalComponent): boolean | null {
        if (controlWrapper.control && this._frame instanceof IEditableFrame) {
            this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for editable right for EditableControl. Editable OwnerFrame isEditing is "${this._frame.isEditing}".`, 10); // ` TODO-rites: remove !!
            return !!this._frame.isEditing;
        }

        return null;
    }

    protected _controlsMultiEditing(controlWrapper: ILogicalComponent): boolean | null {
        if (controlWrapper.control instanceof IMultiEdit && this._frame instanceof IMultiRecordsComponent) {
            return this._frame.isMultiSelecting;
        }
        return null;
    }

    protected _enabledVisualControlsEnabledVisual(controlWrapper: ILogicalComponent): boolean | null {
        const editableControl = controlWrapper.control as EditableControl;

        if (editableControl) {
            const enabled = controlWrapper.getRight(RightType.enabled);
            let editing = controlWrapper.getRight(RightType.editing);

            if (editing == null)
                editing = true;

            let interactive = controlWrapper.getRight(RightType.interactive);
            if (interactive == null)
                interactive = true;

            return enabled && editing && interactive;
        }

        return null;
    }

    protected _linkButtonEnabled(controlWrapper: ILogicalComponent, basicRightResult: boolean): boolean | null {
        const linkButtonConnector = controlWrapper.control as LinkButtonConnector;

        if (linkButtonConnector) {
            const hasSomeParams = linkButtonConnector.params.length > 0 || linkButtonConnector.functions.length > 0;
            const canCallStaticMethods = canCallOnlyStaticMethods(linkButtonConnector.url, linkButtonConnector.functions);

            if (!basicRightResult) {
                return false;
            } else if (canCallStaticMethods) {
                return true;
            } else if (UrlUtils.hasParameters(linkButtonConnector.url) && !this._frame.actualObject && hasSomeParams) {
                return false;
            } else {
                return true;
            }
        }
        return null;
    }

    protected _iInteractiveInteractive(controlWrapper: ILogicalComponent): boolean | null {
        const frame: IFrame = controlWrapper.control instanceof IFrame && controlWrapper.control;
        // Pokud jde o IFrame
        if (frame) {
            return this._iFrameInteractive(frame);
        }

        // Pokud nejede o IFrame
        const iInteractiveControl: IInteractive = controlWrapper.control instanceof IInteractive && controlWrapper.control;
        if (iInteractiveControl) {
            if (iInteractiveControl instanceof Control) {
                return this._iInteractiveControlInteractive(iInteractiveControl);
            }
        }

        return null;
    }

    protected _iFrameInteractive(frame: IFrame): boolean | null {
        let res = null;
        if (frame.ownerForm)
            res = !frame.ownerForm.readOnly;

        if (frame instanceof IMultiRecordsComponent) {
            return (res ?? true) && frame.canEnterMultiEdit;
        }

        return res;
    }

    private _iInteractiveControlInteractive(_interactiveControl: Control): boolean | null {
        //if (interactiveControl.enabled) {
        return /*true*/this._frame.interactive;
        //} else
        //    return false; // Pokud je zakázána právem enabled, interaktivní už nebude
    }

    protected _isFrameVersioned(frame: IFrame): boolean {
        return frame instanceof IVersioned && frame.readVersioned;
    }

    //TODO-rites: debug remove?
    protected _debugPrintMethod(text: string, verbosity: number, depth: number = 0): void {
        if (this._PRINT_CONSOLE_DEBUG_LEVEL < verbosity)
            return;

        let prefix = "";
        for (let i = 0; i < depth; ++i) {
            prefix += " ";
        }
        logger.log(prefix + text);
    }
}
